/**
 * Owner admin — create delivery staff accounts (AppShell via ProtectedRoute).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatApiError } from '../api';
import { deliveryStaffOwnerApi } from '../lib/deliveryStaffApi';

export default function DeliveryStaffPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [origin, setOrigin] = useState('');
  const [form, setForm] = useState({
    username: '',
    password: '',
    display_name: '',
    phone: '',
  });
  const [resetId, setResetId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') setOrigin(window.location.origin);
  }, []);

  const staffLoginPath = '/delivery-staff/login';
  const staffLoginUrl = origin ? `${origin}${staffLoginPath}` : staffLoginPath;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await deliveryStaffOwnerApi.listUsers();
      setUsers(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setError(formatApiError(e, 'Failed to load staff'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const copyText = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`Copied: ${label}`);
      setTimeout(() => setSuccess(''), 2500);
    } catch {
      setError('Could not copy — select the link and copy manually');
    }
  }, []);

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError('');
      await deliveryStaffOwnerApi.createUser({
        username: form.username.trim(),
        password: form.password,
        display_name: form.display_name.trim(),
        phone: form.phone.trim() || undefined,
      });
      setSuccess('Staff account created');
      setForm({ username: '', password: '', display_name: '', phone: '' });
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(formatApiError(e, 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await deliveryStaffOwnerApi.updateUser(user.id, {
        is_active: !user.is_active,
      });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e, 'Update failed'));
    }
  };

  const handleReset = async () => {
    if (!resetId || resetPassword.length < 4) return;
    try {
      await deliveryStaffOwnerApi.resetPassword(resetId, resetPassword);
      setResetId(null);
      setResetPassword('');
      setSuccess('Password reset');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(formatApiError(e, 'Reset failed'));
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Delivery Staff Accounts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create logins for delivery staff. When a staff member takes an order,
        others see &quot;Taken by Name, xx min ago&quot; and cannot take the
        same order. Only the resident sees the real 2-digit code — you and
        staff both pick from 3 options at Confirm delivery.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Staff portal link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy and send to delivery staff (phone or computer). After login
            they get Orders + Order alerts (same PWA setup as{' '}
            <strong>/notifications-help</strong>).
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.25,
              bgcolor: 'grey.50',
            }}
          >
            <Box
              sx={{
                flex: 1,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                wordBreak: 'break-all',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.25,
                py: 1,
                userSelect: 'all',
              }}
            >
              {staffLoginUrl}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() => copyText(staffLoginUrl, 'Staff login')}
                sx={{ textTransform: 'none' }}
              >
                Copy
              </Button>
              {origin && (
                <Tooltip title="Open in new tab">
                  <IconButton
                    size="small"
                    component="a"
                    href={staffLoginPath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Paper>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add staff member
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              label="Display name"
              value={form.display_name}
              onChange={(e) =>
                setForm({ ...form, display_name: e.target.value })
              }
              size="small"
              helperText="Shown on Taken by…"
            />
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              size="small"
              helperText="e.g. rider1"
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              size="small"
            />
            <TextField
              label="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={
                saving ||
                !form.username.trim() ||
                !form.password ||
                !form.display_name.trim()
              }
              sx={{ alignSelf: { sm: 'flex-start' }, mt: { sm: 0.5 } }}
            >
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && users.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.display_name}</TableCell>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>
                        <Chip
                          label={u.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={u.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => toggleActive(u)}
                          sx={{ mr: 1 }}
                        >
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="small" onClick={() => setResetId(u.id)}>
                          Reset password
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No staff accounts yet — add your first rider above
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!resetId} onClose={() => setResetId(null)}>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="New password (min 4 chars)"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetId(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={resetPassword.length < 4}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
